import React, { useState } from 'react';
import { Plus, AlertCircle, Link as LinkIcon, Sparkles } from 'lucide-react';
import { Button } from '../../components/common';
import { apiClient } from '../../api/client';
import { useLanguage } from '../../contexts/LanguageContext';
import { VerificationProgress, type VerifyExtractionResult } from './VerificationProgress';
import { ProductPreviewCard } from './ProductPreviewCard';

export interface UrlSubmitBarProps {
  onWatchCreated?: () => void;
}

type TrackFlowState = 'idle' | 'submitting' | 'verifying' | 'preview';

export const UrlSubmitBar: React.FC<UrlSubmitBarProps> = ({ onWatchCreated }) => {
  const { t } = useLanguage();
  const [urlInput, setUrlInput] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);
  const [flowState, setFlowState] = useState<TrackFlowState>('idle');
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeUrl, setActiveUrl] = useState<string>('');
  const [previewResult, setPreviewResult] = useState<VerifyExtractionResult | null>(null);

  const handleUrlSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInputError(null);

    const trimmed = urlInput.trim();
    if (!trimmed) {
      setInputError('Please enter a product URL.');
      return;
    }

    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setInputError('Please enter a valid HTTP or HTTPS URL.');
        return;
      }
    } catch {
      setInputError('Please enter a valid HTTP or HTTPS URL.');
      return;
    }

    setFlowState('submitting');
    setActiveUrl(trimmed);

    try {
      const { data, error } = await apiClient.POST('/api/v1/verify', {
        body: { url: trimmed },
      });

      if (error || !data) {
        const msg =
          typeof error === 'object' && 'detail' in error
            ? String((error as any).detail)
            : 'Unable to initiate verification for this store.';
        setInputError(msg);
        setFlowState('idle');
        return;
      }

      setActiveJobId(data.job_id);
      setFlowState('verifying');
    } catch (err: any) {
      setInputError(err.message || 'Network error while submitting product URL.');
      setFlowState('idle');
    }
  };

  const handleVerificationComplete = (result: VerifyExtractionResult) => {
    setPreviewResult(result);
    setFlowState('preview');
  };

  const handleVerificationError = (errorMessage: string) => {
    setInputError(errorMessage);
    setFlowState('idle');
    setActiveJobId(null);
  };

  const handleReset = () => {
    setFlowState('idle');
    setActiveJobId(null);
    setActiveUrl('');
    setPreviewResult(null);
    setInputError(null);
    setUrlInput('');
  };

  const handleSuccess = () => {
    handleReset();
    if (onWatchCreated) onWatchCreated();
  };

  return (
    <div className="space-y-4">
      {/* 1. Input Form in idle or submitting state */}
      {flowState === 'idle' || flowState === 'submitting' ? (
        <div className="p-4 sm:p-5 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200/80 dark:border-slate-800 shadow-xs">
          <form onSubmit={handleUrlSubmit} className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
              <label
                htmlFor="track-product-url"
                className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300"
              >
                {t('nav.trackTab')}
              </label>
            </div>

            <div className="flex flex-col sm:flex-row gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 start-0 ps-3.5 flex items-center pointer-events-none text-slate-400">
                  <LinkIcon className="w-4 h-4" />
                </div>
                <input
                  id="track-product-url"
                  type="text"
                  placeholder={t('track.pastePlaceholder')}
                  value={urlInput}
                  onChange={(e) => {
                    setUrlInput(e.target.value);
                    if (inputError) setInputError(null);
                  }}
                  disabled={flowState === 'submitting'}
                  className="w-full ps-10 pe-4 py-2.5 text-sm rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all"
                />
              </div>

              <Button
                type="submit"
                variant="primary"
                size="md"
                isLoading={flowState === 'submitting'}
                className="whitespace-nowrap sm:w-auto w-full"
              >
                <Plus className="w-4 h-4 me-1.5" />
                {flowState === 'submitting' ? t('track.tracking') : t('track.trackPrice')}
              </Button>
            </div>

            {inputError && (
              <div className="flex items-center gap-2 text-xs text-rose-600 dark:text-rose-400 pt-1">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{inputError}</span>
              </div>
            )}
          </form>
        </div>
      ) : null}

      {/* 2. Verification in progress */}
      {flowState === 'verifying' && activeJobId && (
        <VerificationProgress
          jobId={activeJobId}
          url={activeUrl}
          onComplete={handleVerificationComplete}
          onError={handleVerificationError}
          onCancel={handleReset}
        />
      )}

      {/* 3. Extracted preview ready for confirmation */}
      {flowState === 'preview' && previewResult && (
        <ProductPreviewCard
          url={activeUrl}
          jobId={activeJobId || ''}
          preview={previewResult}
          onWatchCreated={handleSuccess}
          onCancel={handleReset}
        />
      )}
    </div>
  );
};
